import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Query,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { z } from "zod";

import { db } from "integrations/firebase/firestore";

import { newTableNames } from "./board";
import {
  DEFAULT_SEATING_NAME,
  publishedTableSchema,
  seatingSchema,
  seatingTableSchema,
  seatSchema,
  sortSeatings,
  sortTables,
  type PublishedTable,
  type Seat,
  type Seating,
  type SeatingTable,
} from "./schemas";

export type LiveState<T> =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: T[] };

const seatingsPath = (eventId: string) =>
  ["events", eventId, "seatings"] as const;

/**
 * A live collection read, parsed per document; a document that fails to
 * parse is skipped rather than failing the list. `buildQuery` null means
 * "nothing to read", which resolves to an empty list.
 */
function useLiveCollection<T>(
  buildQuery: (() => Query) | null,
  schema: z.ZodType<T>,
  key: string,
): LiveState<T> {
  const [state, setState] = useState<LiveState<T>>({ status: "loading" });

  useEffect(() => {
    if (!buildQuery) {
      setState({ status: "success", data: [] });
      return;
    }
    setState({ status: "loading" });
    return onSnapshot(
      buildQuery(),
      (snapshot) => {
        const data: T[] = [];
        snapshot.forEach((item) => {
          const result = schema.safeParse({ id: item.id, ...item.data() });
          if (result.success) data.push(result.data);
        });
        setState({ status: "success", data });
      },
      (error) =>
        setState({
          status: "error",
          error: error instanceof Error ? error.message : "Failed to load",
        }),
    );
    // `key` stands for the query: the builder is a fresh closure per render.
  }, [key]);

  return state;
}

/**
 * The event's seatings, live. Organisers read all of them; an attendee
 * passes `publishedOnly`, which is the only query the rules let a member run.
 */
export function useSeatings(
  eventId: string,
  { publishedOnly = false, enabled = true } = {},
): LiveState<Seating> {
  const state = useLiveCollection(
    enabled
      ? () =>
          publishedOnly
            ? query(
                collection(db, ...seatingsPath(eventId)),
                where("status", "==", "PUBLISHED"),
              )
            : query(collection(db, ...seatingsPath(eventId)))
      : null,
    seatingSchema,
    `seatings:${eventId}:${publishedOnly}:${enabled}`,
  );
  return useMemo(
    () =>
      state.status === "success"
        ? { ...state, data: sortSeatings(state.data) }
        : state,
    [state],
  );
}

export function useSeatingTables(
  eventId: string,
  seatingId: string | null,
): LiveState<SeatingTable> {
  const state = useLiveCollection(
    seatingId
      ? () =>
          query(collection(db, ...seatingsPath(eventId), seatingId, "tables"))
      : null,
    seatingTableSchema,
    `tables:${eventId}:${seatingId}`,
  );
  return useMemo(
    () =>
      state.status === "success"
        ? { ...state, data: sortTables(state.data) }
        : state,
    [state],
  );
}

export function useSeats(
  eventId: string,
  seatingId: string | null,
): LiveState<Seat> {
  return useLiveCollection(
    seatingId
      ? () =>
          query(collection(db, ...seatingsPath(eventId), seatingId, "seats"))
      : null,
    seatSchema,
    `seats:${eventId}:${seatingId}`,
  );
}

/**
 * A seating's published dinner tables. With `attendeeId`, only the one that
 * seats this attendee (array-contains), which is how an attendee finds their
 * own table.
 */
export function usePublishedTables(
  eventId: string,
  seatingId: string | null,
  attendeeId?: string,
): LiveState<PublishedTable> {
  const state = useLiveCollection(
    seatingId
      ? () => {
          const tables = collection(
            db,
            ...seatingsPath(eventId),
            seatingId,
            "published",
          );
          return attendeeId
            ? query(tables, where("attendeeIds", "array-contains", attendeeId))
            : query(tables);
        }
      : null,
    publishedTableSchema,
    `published:${eventId}:${seatingId}:${attendeeId ?? ""}`,
  );
  return useMemo(
    () =>
      state.status === "success"
        ? { ...state, data: sortTables(state.data) }
        : state,
    [state],
  );
}

export interface SeatingWrites {
  error: string | null;
  saving: boolean;
  createSeating: (name: string, displayOrder: number) => Promise<string | null>;
  renameSeating: (seatingId: string, name: string) => Promise<boolean>;
  createTables: (
    seatingId: string,
    count: number,
    seats: number,
    existing: SeatingTable[],
  ) => Promise<boolean>;
  updateTable: (
    seatingId: string,
    tableId: string,
    change: Partial<Pick<SeatingTable, "name" | "capacity">>,
  ) => Promise<boolean>;
  deleteTable: (
    seatingId: string,
    tableId: string,
    seatsAtTable: string[],
  ) => Promise<boolean>;
  setSeat: (
    seatingId: string,
    attendeeId: string,
    tableId: string | null,
    pinned: boolean,
  ) => Promise<boolean>;
}

/**
 * The organiser's direct writes. Everything past these (suggest, publish,
 * unpublish, delete) is an admin command.
 */
export function useSeatingWrites(eventId: string): SeatingWrites {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const run = useCallback(
    async <T>(work: () => Promise<T>, fallback: T): Promise<T> => {
      setSaving(true);
      setError(null);
      try {
        return await work();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not save");
        return fallback;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const createSeating = useCallback<SeatingWrites["createSeating"]>(
    (name, displayOrder) =>
      run(async () => {
        const ref = doc(collection(db, ...seatingsPath(eventId)));
        await setDoc(ref, {
          name: name.trim() || DEFAULT_SEATING_NAME,
          displayOrder,
          status: "DRAFT",
          createdAt: serverTimestamp(),
        });
        return ref.id;
      }, null),
    [eventId, run],
  );

  const renameSeating = useCallback<SeatingWrites["renameSeating"]>(
    (seatingId, name) =>
      run(async () => {
        await updateDoc(doc(db, ...seatingsPath(eventId), seatingId), {
          name: name.trim(),
        });
        return true;
      }, false),
    [eventId, run],
  );

  const createTables = useCallback<SeatingWrites["createTables"]>(
    (seatingId, count, seats, existing) =>
      run(async () => {
        const batch = writeBatch(db);
        const tables = collection(
          db,
          ...seatingsPath(eventId),
          seatingId,
          "tables",
        );
        const start = existing.reduce(
          (max, table) => Math.max(max, table.displayOrder + 1),
          0,
        );
        const names = newTableNames(existing, count);
        for (let i = 0; i < count; i += 1) {
          batch.set(doc(tables), {
            name: names[i],
            displayOrder: start + i,
            capacity: seats,
            createdAt: serverTimestamp(),
          });
        }
        await batch.commit();
        return true;
      }, false),
    [eventId, run],
  );

  const updateTable = useCallback<SeatingWrites["updateTable"]>(
    (seatingId, tableId, change) =>
      run(async () => {
        await updateDoc(
          doc(db, ...seatingsPath(eventId), seatingId, "tables", tableId),
          change,
        );
        return true;
      }, false),
    [eventId, run],
  );

  const deleteTable = useCallback<SeatingWrites["deleteTable"]>(
    (seatingId, tableId, seatsAtTable) =>
      run(async () => {
        // One batch: the rules refuse a seat at a table that does not exist,
        // so the guests at it become "not seated yet" with the table.
        const batch = writeBatch(db);
        for (const attendeeId of seatsAtTable) {
          batch.set(
            doc(db, ...seatingsPath(eventId), seatingId, "seats", attendeeId),
            { tableId: null, pinned: false, updatedAt: serverTimestamp() },
          );
        }
        batch.delete(
          doc(db, ...seatingsPath(eventId), seatingId, "tables", tableId),
        );
        await batch.commit();
        return true;
      }, false),
    [eventId, run],
  );

  const setSeat = useCallback<SeatingWrites["setSeat"]>(
    (seatingId, attendeeId, tableId, pinned) =>
      run(async () => {
        await setDoc(
          doc(db, ...seatingsPath(eventId), seatingId, "seats", attendeeId),
          { tableId, pinned, updatedAt: serverTimestamp() },
        );
        return true;
      }, false),
    [eventId, run],
  );

  return {
    error,
    saving,
    createSeating,
    renameSeating,
    createTables,
    updateTable,
    deleteTable,
    setSeat,
  };
}
